import React, { useState, useEffect } from 'react';

interface PhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  photos: Array<{
    id: string;
    title: string;
    imageUrl: string;
    originalUrl: string;
    date?: string;
    description?: string;
  }>;
  initialIndex?: number;
  currentIndex?: number;
  onNavigate?: (direction: 'next' | 'prev') => void;
  totalPhotos?: number;
}

const PhotoModal: React.FC<PhotoModalProps> = ({ 
  isOpen, 
  onClose, 
  photos, 
  initialIndex = 0,
  currentIndex: externalCurrentIndex,
  onNavigate,
  totalPhotos
}) => {
  const [currentIndex, setCurrentIndex] = useState(externalCurrentIndex ?? initialIndex);
  const [currentImageUrl, setCurrentImageUrl] = useState(photos[externalCurrentIndex ?? initialIndex]?.imageUrl || '');

  useEffect(() => {
    const index = externalCurrentIndex ?? initialIndex;
    setCurrentIndex(index);
    setCurrentImageUrl(photos[index]?.imageUrl || '');
  }, [externalCurrentIndex, initialIndex, photos]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setCurrentImageUrl(photos[newIndex]?.imageUrl || '');
      onNavigate?.('prev');
    }
  };

  const handleNext = () => {
    if (currentIndex < photos.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setCurrentImageUrl(photos[newIndex]?.imageUrl || '');
    }
    // Always call onNavigate for next (parent will handle loading more photos)
    onNavigate?.('next');
  };

  const handleDownload = async () => {
    const currentPhoto = photos[currentIndex];
    if (!currentPhoto?.originalUrl) return;

    try {
      // First try to fetch the image as a blob
      const response = await fetch(currentPhoto.originalUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      
      const blob = await response.blob();
      
      // Create a blob URL and download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Extract file extension from original URL or default to .jpg
      const urlParts = currentPhoto.originalUrl.split('.');
      const extension = urlParts.length > 1 ? urlParts[urlParts.length - 1].split('?')[0] : 'jpg';
      const filename = `${currentPhoto.id || `photo_${currentIndex + 1}`}.${extension}`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct download attempt
      const link = document.createElement('a');
      link.href = currentPhoto.originalUrl;
      link.download = currentPhoto.id || `photo_${currentIndex + 1}.jpg`;
      link.target = '_blank'; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      {/* Header */}
      <div style={{
        position: 'absolute',
        top: 20,
        left: 20,
        right: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white'
      }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
          {currentPhoto.title}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={handleDownload}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
            title="Download original photo"
          >
            📥 Download
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '2rem',
              cursor: 'pointer',
              padding: '10px'
            }}
          >
            ×
          </button>
        </div>
      </div>

      {/* Image Container */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <img
          src={currentImageUrl}
          alt={currentPhoto.title}
          style={{
            maxWidth: '90%',
            maxHeight: '80%',
            objectFit: 'contain'
          }}
        />
      </div>

      {/* Navigation */}
      {photos.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: 20,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: '20px'
        }}>
          <button
            onClick={handlePrevious}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            ← Previous
          </button>
          <span style={{ color: 'white', padding: '10px' }}>
            {currentIndex + 1} / {totalPhotos || photos.length}
          </span>
          <button
            onClick={handleNext}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default PhotoModal; 